import React, { useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers';

interface ClaimDocumentUploadProps {
  show: boolean;
  handleClose: () => void;
  onSaveSuccess?: () => void;
  intimationId: number;
  memberId: string;
  employeeId: string | undefined;
}

interface UploadFile extends File {
  preview?: string;
  uploadProgress?: number;
}

export const ClaimDocumentUpload: React.FC<ClaimDocumentUploadProps> = ({
  show,
  handleClose,
  onSaveSuccess,
  intimationId,
  memberId,
  employeeId,
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<{ name: string; permanentFile: string; fileUrl: string }[]>([]);

  const acceptedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file: File): string | null => {
    if (!acceptedFileTypes.includes(file.type)) {
      return `File type not supported: ${file.name}`;
    }
    if (file.size > maxFileSize) {
      return `File too large (max 10MB): ${file.name}`;
    }
    return null;
  };

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return;

    const validFiles: UploadFile[] = [];
    const errors: string[] = [];

    Array.from(newFiles).forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setUploadError(errors.join('\n'));
      return;
    }

    setUploadError(null);
    setSaveSuccess(false);

    // Start uploading valid files to storage immediately
    if (validFiles.length > 0) {
      uploadFilesToStorage(validFiles);
    }
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      e.target.value = '';
    },
    [handleFiles]
  );

  const removeFile = useCallback((index: number) => {
    const removedFile = files[index];
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadedDocuments((prev) => prev.filter((doc) => doc.name !== removedFile.name));
    setSaveSuccess(false);
  }, [files]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string): string => {
    if (fileType.includes('pdf')) return '/media/icons/duotune/files/fil003.svg';
    if (fileType.includes('image')) return '/media/icons/duotune/files/fil010.svg';
    if (fileType.includes('word')) return '/media/icons/duotune/files/fil005.svg';
    return '/media/icons/duotune/files/fil008.svg';
  };

  const [rawDebugResponse, setRawDebugResponse] = useState<string | null>(null);

  const uploadSingleFile = async (file: UploadFile): Promise<{ permanentFile: string; fileUrl: string } | null> => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    console.log('Uploading file:', file.name, file.type, file.size);

    const response = await fetch('https://qmtplus.futurisk.in/File/PermanentUpload', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });

    let responseText = '';
    try {
      responseText = await response.text();
    } catch (e) {
      // ignore
    }

    console.log('Upload response status:', response.status, responseText);

    if (!response.ok) {
      throw new Error(`Upload failed for ${file.name}: ${response.status} ${response.statusText}\n${responseText}`);
    }

    let result: any = {};
    try {
      result = JSON.parse(responseText);
    } catch (e) {
      throw new Error(`Upload failed for ${file.name}: invalid JSON response\n${responseText}`);
    }

    if (result.Error) {
      throw new Error(`Upload failed for ${file.name}: ${result.Error.Message}`);
    }

    if (!result.PermanentFile) {
      throw new Error(`Upload failed for ${file.name}: no permanent file returned`);
    }

    return {
      permanentFile: result.PermanentFile as string,
      fileUrl: result.FileUrl as string,
    };
  };

  const uploadFilesToStorage = async (filesToUpload: UploadFile[]) => {
    setUploading(true);
    setUploadError(null);

    try {
      const uploadResults: { name: string; permanentFile: string; fileUrl: string }[] = [];

      for (const file of filesToUpload) {
        const result = await uploadSingleFile(file);
        if (result) {
          uploadResults.push({ name: file.name, permanentFile: result.permanentFile, fileUrl: result.fileUrl });
        }
      }

      setFiles((prev) => [...prev, ...filesToUpload]);
      setUploadedDocuments((prev) => [...prev, ...uploadResults]);
    } catch (error: any) {
      console.error(error);
      setUploadError(error.message || 'Document upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (uploadedDocuments.length === 0) {
      setUploadError('Please upload at least one document before submitting.');
      return;
    }

    setSaving(true);
    setUploadError(null);

    try {
      const payload = {
        intimationId,
        memberId,
        employeeId,
        documents: uploadedDocuments.map((doc) => ({
          name: doc.name,
          permanentFile: doc.permanentFile,
          fileUrl: doc.fileUrl,
        })),
      };

      console.log('Saving documents to webhook:', payload);

      const response = await fetch('https://etl.futurisk.in/webhook/6f1d955f-34fa-4966-8f88-0fa9f7021ab3', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to save documents: ${response.status} ${response.statusText}`);
      }

      setSaveSuccess(true);

      // Refresh parent ClaimHubModel after short delay
      setTimeout(() => {
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        handleCloseModal();
      }, 1000);
    } catch (error: any) {
      console.error(error);
      setUploadError(error.message || 'Failed to save document list. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setFiles([]);
    setUploadedDocuments([]);
    setPendingFiles([]);
    setUploadError(null);
    setRawDebugResponse(null);
    setSaveSuccess(false);
    setIsDragging(false);
    handleClose();
  };

  return (
    <Modal
      className='modal fade'
      show={show}
      onHide={handleCloseModal}
      centered
      size='lg'
      backdrop='static'
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Upload Claim Documents</h5>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={handleCloseModal}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>

        <div className='modal-body'>
          <div className='mb-5'>
            <div className='d-flex justify-content-between mb-2'>
              <span className='fw-bold text-dark'>Intimation ID:</span>
              <span className='text-gray-700'>#{intimationId}</span>
            </div>
            <div className='d-flex justify-content-between mb-2'>
              <span className='fw-bold text-dark'>Member ID:</span>
              <span className='text-gray-700'>{memberId}</span>
            </div>
          </div>

          <div
            className={`dropzone dropzone-queue mb-5 ${isDragging ? 'bg-light-primary border-primary' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
              border: '2px dashed #e4e6ef',
              borderRadius: '0.625rem',
              padding: '2rem',
              textAlign: 'center',
              transition: 'all 0.2s ease',
            }}
          >
            <input
              type='file'
              id='claim-document-upload'
              multiple
              accept='.jpg,.jpeg,.png,.pdf,.doc,.docx'
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />
            <label htmlFor='claim-document-upload' className='cursor-pointer w-100 mb-0'>
              <div className='mb-4'>
                <KTSVG path='/media/icons/duotune/files/fil025.svg' className='svg-icon-3x svg-icon-primary' />
              </div>
              <div className='text-gray-600 fw-bold mb-2'>
                Drop files here or click to upload
              </div>
              <div className='text-gray-400 fs-7'>
                Supports: JPG, PNG, PDF, DOC, DOCX (Max 10MB per file)
              </div>
            </label>
          </div>

          {uploadError && (
            <div className='alert alert-danger mb-5'>
              <div className='d-flex flex-column'>
                {uploadError.split('\n').map((err, idx) => (
                  <span key={idx}>{err}</span>
                ))}
              </div>
            </div>
          )}

          {rawDebugResponse && (
            <div className='alert alert-warning mb-5'>
              <div className='fw-bold mb-2'>Debug Response:</div>
              <pre className='mb-0' style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                {rawDebugResponse}
              </pre>
            </div>
          )}

          {saveSuccess && (
            <div className='alert alert-success mb-5'>
              <div className='fw-bold mb-2'>Documents saved successfully</div>
            </div>
          )}

          {uploadedDocuments.length > 0 && (
            <div className='alert alert-light mb-5'>
              <div className='fw-bold mb-2'>Documents Ready for Submission</div>
              <div className='d-flex flex-column'>
                {uploadedDocuments.map((file, idx) => (
                  <span key={idx} className='text-gray-700'>
                    {file.name}: <code>{file.permanentFile}</code>
                  </span>
                ))}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className='mb-5'>
              <h6 className='fw-bold text-dark mb-3'>
                Selected Files ({files.length})
              </h6>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 gy-2'>
                  <tbody>
                    {files.map((file, index) => (
                      <tr key={`${file.name}-${index}`}>
                        <td className='ps-0'>
                          <div className='d-flex align-items-center'>
                            <KTSVG
                              path={getFileIcon(file.type)}
                              className='svg-icon-2x svg-icon-primary me-3'
                            />
                            <div className='d-flex flex-column'>
                              <span className='text-gray-800 fw-bold'>{file.name}</span>
                              <span className='text-gray-400 fs-7'>{formatFileSize(file.size)}</span>
                            </div>
                          </div>
                        </td>
                        <td className='text-end pe-0'>
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light-danger'
                            onClick={() => removeFile(index)}
                            disabled={uploading}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen034.svg' className='svg-icon-2' />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <button
            type='button'
            className='btn btn-light'
            onClick={handleCloseModal}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleSubmit}
            disabled={uploading || saving || uploadedDocuments.length === 0}
          >
            {!saving && <span className='indicator-label'>Submit Documents</span>}
            {saving && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Saving...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
